import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Camera, ImageOff, Loader2, Trash2 } from 'lucide-react'
import { Button } from 'components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { cn } from 'lib/utils'
import {
	initializeCaptureImages,
	deleteCaptureImage,
	type CaptureImageRow,
} from 'slices/captureImageSlice'
import FieldShell from '../components/FieldShell'
import { IMAGE_RETENTION_DAYS } from 'constants/imageRetention'
import CaptureSessionDialog from '../components/CaptureSessionDialog'

type Filter = 'all' | 'unused'

/** Label AND day: the same label on two visits is two rolls. */
interface Session {
	key: string
	label: string
	capturedAt: string
	images: CaptureImageRow[]
}

const groupSessions = (images: CaptureImageRow[]): Session[] => {
	const sessions = new Map<string, Session>()

	for (const image of images) {
		if (!image) continue
		const when = new Date(image.capturedAt)
		const day = Number.isNaN(when.getTime()) ? 'unknown' : when.toISOString().slice(0, 10)
		const label = image.label || 'Untitled'
		const key = `${label}::${day}`

		if (!sessions.has(key)) {
			sessions.set(key, { key, label, capturedAt: image.capturedAt, images: [] })
		}
		sessions.get(key)!.images.push(image)
	}

	return [...sessions.values()].sort(
		(a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime(),
	)
}

const formatDay = (iso: string): string => {
	const when = new Date(iso)
	if (Number.isNaN(when.getTime())) return ''
	return when.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Every image captured in the field, before it belongs to anything.
 *
 * Documenting a space means twenty photographs in a few minutes, and every
 * photograph in this app used to need a note to live in. So a rep either
 * stopped to write one per shot or lost track of which pictures went with what.
 * This is the roll: capture now, file later.
 *
 * Grouped by session rather than shown as one flat wall, because that is how
 * they were taken and how they will be looked for. Four across, which is the
 * most a 390px column fits while a thumbnail is still recognisable.
 */
const FieldImages = () => {
	const dispatch = useDispatch()
	const { entities, ids, isLoading, isInitialized, error } = useSelector(
		(state: any) => state.captureImages,
	)

	const [capturing, setCapturing] = useState(false)
	const [filter, setFilter] = useState<Filter>('all')
	const [viewing, setViewing] = useState<CaptureImageRow | null>(null)
	const [deleting, setDeleting] = useState(false)

	const load = useCallback(() => {
		;(dispatch as any)(initializeCaptureImages(undefined))
	}, [dispatch])

	useEffect(() => {
		if (!isInitialized) load()
	}, [isInitialized, load])

	const images = useMemo<CaptureImageRow[]>(
		() => (ids || []).map((id: string) => entities[id]).filter(Boolean),
		[entities, ids],
	)

	const shown = useMemo(
		() => (filter === 'unused' ? images.filter(image => !image.usedAt) : images),
		[images, filter],
	)

	const sessions = useMemo(() => groupSessions(shown), [shown])
	const unusedCount = useMemo(() => images.filter(image => !image.usedAt).length, [images])

	const removeImage = useCallback(async () => {
		if (!viewing) return
		setDeleting(true)
		const action: any = await (dispatch as any)(deleteCaptureImage(viewing._id))
		setDeleting(false)
		// A rejected thunk resolves, so closing regardless would report a delete
		// that did not happen.
		if (action?.meta?.requestStatus === 'rejected') return
		setViewing(null)
	}, [viewing, dispatch])

	return (
		<FieldShell title="Images">
			<div className="pb-6">
				<div className="flex items-center gap-2 px-3 py-3">
					<Button
						variant="primary"
						isInput
						onClick={() => setCapturing(true)}
						className="flex-1"
					>
						<Camera className="mr-2 h-4 w-4" />
						Capture Images
					</Button>
				</div>

				{/* Offered only once something has been used, or it is a filter
				    that can never change anything. */}
				{images.length > 0 && unusedCount !== images.length && (
					<div className="flex gap-1 px-3 pb-2">
						{(['all', 'unused'] as Filter[]).map(option => (
							<button
								key={option}
								type="button"
								onClick={() => setFilter(option)}
								aria-pressed={filter === option}
								className={cn(
									'rounded-full px-3 py-1 text-xs font-medium',
									filter === option ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600',
								)}
							>
								{option === 'all' ? `All ${images.length}` : `Unused ${unusedCount}`}
							</button>
						))}
					</div>
				)}

				{error && (
					<p role="alert" className="px-4 py-2 text-sm text-destructive">
						{error}
					</p>
				)}

				{isLoading && images.length === 0 ? (
					<p className="flex items-center gap-2 px-4 py-6 text-sm text-gray-500">
						<Loader2 className="h-4 w-4 animate-spin" />
						Loading your images…
					</p>
				) : sessions.length === 0 ? (
					<div className="m-3 rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center">
						<ImageOff className="mx-auto mb-2 h-6 w-6 text-gray-300" />
						<p className="text-sm font-medium text-gray-800">
							{filter === 'unused' ? 'Everything here is in a note' : 'No images yet'}
						</p>
						<p className="mt-1 text-sm text-gray-500">
							{filter === 'unused'
								? 'Switch to All to see the whole library.'
								: 'Capture a set now and file it into a note later.'}
						</p>
					</div>
				) : (
					sessions.map(session => (
						<section key={session.key} className="mb-4">
							<h2 className="flex items-baseline justify-between gap-2 px-4 pb-1.5">
								<span className="truncate text-sm font-medium text-gray-900">
									{session.label}
								</span>
								<span className="shrink-0 text-xs text-gray-400">
									{formatDay(session.capturedAt)} · {session.images.length}
								</span>
							</h2>
							{/* Four across: the most a 390px column fits while a
							    thumbnail is still recognisable. */}
							<ul className="grid grid-cols-4 gap-1 px-3">
								{session.images.map(image => (
									<li key={image._id}>
										<button
											type="button"
											onClick={() => setViewing(image)}
											aria-label={`${session.label}, ${image.usedAt ? 'used in a note' : 'not yet used'}`}
											className="relative block w-full"
										>
											<img
												src={image.url}
												alt=""
												loading="lazy"
												className="aspect-square w-full rounded-md border border-gray-200 object-cover"
											/>
											{image.usedAt && (
												// Dimmed rather than badged: at this size a badge
												// covers a quarter of the picture.
												<span className="absolute inset-0 rounded-md bg-white/45" />
											)}
										</button>
									</li>
								))}
							</ul>
						</section>
					))
				)}
			</div>

			<CaptureSessionDialog
				open={capturing}
				onClose={() => setCapturing(false)}
				onSaved={load}
			/>

			{/* Full screen, and the only place delete is offered: it is not a
			    thing to do by accident from a grid of thumbnails. */}
			<Dialog open={Boolean(viewing)} onClose={() => setViewing(null)}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle className="truncate">{viewing?.label}</DialogTitle>
						<DialogDescription>
							{viewing ? formatDay(viewing.capturedAt) : ''}
							{viewing?.usedAt ? ' · already in a note' : ''}
						</DialogDescription>
					</DialogHeader>
					<div className="p-5 pt-2">
						{viewing && (
							<img
								src={viewing.url}
								alt={viewing.label}
								className="max-h-[60svh] w-full rounded-md object-contain"
							/>
						)}
						<Button
							variant="ghost"
							isInput
							onClick={removeImage}
							disabled={deleting}
							className="mt-3 w-full text-destructive"
						>
							{deleting ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Removing…
								</>
							) : (
								<>
									<Trash2 className="mr-2 h-4 w-4" />
									Remove from library
								</>
							)}
						</Button>
						{/*
							Said here rather than behind a confirmation. This is a
							one-tap delete of a single photo on a phone, and a modal for
							each one would be the wrong trade; but the user should still
							know the file is not gone forever the instant they tap, which
							until now it never was in either direction. MYD-463.
						*/}
						<p className="mt-2 text-center text-xs text-gray-400">
							Kept for {IMAGE_RETENTION_DAYS} days, then permanently destroyed.
						</p>
						{viewing?.usedAt && (
							<p className="mt-1 text-center text-xs text-gray-400">
								Any note already using this photo keeps it.
							</p>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</FieldShell>
	)
}

export default FieldImages
